import React from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const KpiCard: React.FC<{ label: string; value: number | string; hint?: string }> = ({ label, value, hint }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" display="block">{hint}</Typography>}
    </CardContent>
  </Card>
);

const CallQaOverviewPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.read');

  const { data, isLoading } = useQuery({
    queryKey: ['callQaOverview'],
    queryFn: () => apiClient.callQa.getOverview(),
    enabled: canRead,
  });

  if (!canRead) {
    return <Alert severity="warning">Call QA overview requires call_qa.read.</Alert>;
  }

  const overview = data?.data?.data;
  if (isLoading) return <CircularProgress size={24} />;

  const avgHint = overview?.average_qa_sample_insufficient
    ? `Sample n=${overview?.average_qa_sample_size || 0} — insufficient for team quality claims`
    : overview?.average_qa_sample_size
      ? `n=${overview.average_qa_sample_size}`
      : undefined;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Call QA overview — transcription and evidence-based global scorecard scoring.
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={6} md={4} lg={2}><KpiCard label="Today Calls" value={overview?.today_calls ?? 0} /></Grid>
        <Grid item xs={6} md={4} lg={2}><KpiCard label="Recordings Available" value={overview?.recordings_available ?? 0} /></Grid>
        <Grid item xs={6} md={4} lg={2}><KpiCard label="Unlinked Calls" value={overview?.unlinked_calls ?? 0} /></Grid>
        <Grid item xs={6} md={4} lg={2}><KpiCard label="Calls Scored" value={overview?.calls_scored ?? 0} /></Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Average QA" value={overview?.average_qa ?? '—'} hint={avgHint} />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Calls Needing Review" value={overview?.calls_needing_review ?? 0} />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Critical Failures" value={overview?.critical_failures ?? 0} />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Unscored Calls" value={overview?.unscored_calls ?? 0} />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Pending Transcriptions" value={overview?.pending_transcriptions ?? 0} />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard label="Pending QA" value={overview?.pending_qa ?? 0} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CallQaOverviewPage;
